import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useLang } from "../contexts/LangContext";
import { cases } from "../data/cases";
import FadeIn from "../components/FadeIn";

const CaseDetail = () => {
    const { slug } = useParams();
    const { t, lang } = useLang();
    const caseItem = cases.find((c) => c.slug === slug);

    if (!caseItem) return <Navigate to="/cases" replace />;

    const idx = cases.findIndex((c) => c.slug === slug);
    const next = cases[(idx + 1) % cases.length];

    return (
        <main data-testid="page-case-detail" className="bg-white text-[#111111]">
            {/* Hero */}
            <section className="relative h-[100vh] min-h-[600px] w-full overflow-hidden">
                <img
                    src={caseItem.hero}
                    alt={caseItem.title[lang]}
                    className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 hero-vignette" />
                <div className="relative h-full max-w-[1440px] mx-auto px-6 md:px-12 flex flex-col text-white">
                    <div className="pt-28 md:pt-36">
                        <Link
                            to="/cases"
                            data-testid="case-back-link"
                            className="text-[11px] tracking-luxury uppercase inline-flex items-center gap-2 link-underline"
                        >
                            <ArrowLeft size={14} /> {t.caseDetail.backToCases}
                        </Link>
                    </div>
                    <FadeIn className="mt-auto pb-16 md:pb-24">
                        <div className="text-[11px] tracking-luxury uppercase mb-6">
                            {caseItem.location[lang]}  ·  {caseItem.year}
                        </div>
                        <h1 className="font-serif font-light text-6xl md:text-8xl lg:text-9xl leading-[0.92] tracking-tight max-w-5xl">
                            {caseItem.title[lang]}
                        </h1>
                        <p className="mt-6 font-serif italic text-xl md:text-2xl max-w-2xl text-white/90">
                            {caseItem.subtitle[lang]}
                        </p>
                    </FadeIn>
                </div>
            </section>

            {/* Meta */}
            <section className="border-b border-[#EAE8E4] py-12 md:py-16">
                <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid grid-cols-2 md:grid-cols-4 gap-8">
                    {[
                        { label: t.caseDetail.location, value: caseItem.location[lang] },
                        { label: t.caseDetail.year, value: caseItem.year },
                        { label: t.caseDetail.scope, value: caseItem.scope[lang] },
                        { label: t.caseDetail.architect, value: caseItem.architect },
                    ].map((item, i) => (
                        <FadeIn key={i} delay={i * 0.05}>
                            <div className="text-[11px] tracking-luxury uppercase text-[#888] mb-2">
                                {item.label}
                            </div>
                            <div className="font-serif text-lg md:text-xl">
                                {item.value}
                            </div>
                        </FadeIn>
                    ))}
                </div>
            </section>

            {/* Overview */}
            <section className="py-24 md:py-32">
                <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid grid-cols-12 gap-6">
                    <FadeIn className="col-span-12 md:col-span-3">
                        <div className="text-[11px] tracking-luxury uppercase text-[#888]">
                            — {t.caseDetail.overview}
                        </div>
                    </FadeIn>
                    <FadeIn delay={0.1} className="col-span-12 md:col-span-8">
                        {caseItem.body && (
                            <>
                                <p className="font-serif font-light text-2xl md:text-4xl leading-[1.2] tracking-tight">
                                    {caseItem.body[lang][0]}
                                </p>
                                {caseItem.body[lang].slice(1).map((para, i) => (
                                    <p
                                        key={i}
                                        className="mt-8 text-base md:text-lg text-[#555] leading-relaxed max-w-2xl"
                                    >
                                        {para}
                                    </p>
                                ))}
                            </>
                        )}
                    </FadeIn>
                </div>
            </section>

            {/* Gallery — full asymmetric grid using all gallery images */}
            <section className="pb-24">
                <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid grid-cols-12 gap-6 md:gap-10">
                    <FadeIn className="col-span-12 md:col-span-8">
                        <div className="aspect-[16/10] overflow-hidden bg-[#EBE8E3]">
                            <img
                                src={caseItem.gallery[0]}
                                alt=""
                                className="w-full h-full object-cover"
                            />
                        </div>
                    </FadeIn>
                    {caseItem.gallery.slice(1).map((src, i) => {
                        const patterns = [
                            "col-span-12 md:col-span-4 md:mt-32",
                            "col-span-12 md:col-span-5 md:col-start-2",
                            "col-span-12 md:col-span-6 md:col-start-7 md:mt-12",
                            "col-span-12 md:col-span-7",
                            "col-span-12 md:col-span-4 md:col-start-9 md:mt-16",
                            "col-span-12 md:col-span-9 md:col-start-2",
                            "col-span-12 md:col-span-8",
                        ];
                        const aspects = [
                            "aspect-[3/4]",
                            "aspect-[4/5]",
                            "aspect-[4/3]",
                            "aspect-[3/2]",
                            "aspect-[4/5]",
                            "aspect-[16/9]",
                            "aspect-[16/10]",
                        ];
                        return (
                            <FadeIn
                                key={i}
                                delay={(i % 3) * 0.08}
                                className={patterns[i % patterns.length]}
                            >
                                <div
                                    className={`${aspects[i % aspects.length]} overflow-hidden bg-[#EBE8E3]`}
                                >
                                    <img
                                        src={src}
                                        alt=""
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                            </FadeIn>
                        );
                    })}
                </div>
            </section>

            {/* Materials */}
            <section className="bg-[#F9F8F6] py-24 md:py-32">
                <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid grid-cols-12 gap-6">
                    <FadeIn className="col-span-12 md:col-span-3">
                        <div className="text-[11px] tracking-luxury uppercase text-[#888]">
                            — {t.caseDetail.materials}
                        </div>
                    </FadeIn>
                    <FadeIn delay={0.1} className="col-span-12 md:col-span-9">
                        <ul className="font-serif font-light text-3xl md:text-5xl leading-[1.2] tracking-tight space-y-3">
                            {caseItem.materials[lang].map((m, i) => (
                                <li
                                    key={i}
                                    className="flex items-baseline gap-6"
                                >
                                    <span className="editorial-number text-base not-italic">
                                        {String(i + 1).padStart(2, "0")}
                                    </span>
                                    <span>{m}</span>
                                </li>
                            ))}
                        </ul>
                    </FadeIn>
                </div>
            </section>

            {/* Next */}
            <section className="bg-[#0F0F0E] text-white py-24">
                <Link
                    to={`/cases/${next.slug}`}
                    data-testid="next-case-link"
                    className="block group max-w-[1440px] mx-auto px-6 md:px-12"
                >
                    <div className="text-[11px] tracking-luxury uppercase text-white/60 mb-4">
                        {t.caseDetail.nextProject}
                    </div>
                    <div className="flex items-end justify-between gap-6 flex-wrap">
                        <h3 className="font-serif font-light text-5xl md:text-8xl leading-[0.95] tracking-tight">
                            {next.title[lang]}
                        </h3>
                        <div className="inline-flex items-center gap-3 text-[11px] tracking-luxury uppercase opacity-80 group-hover:opacity-100">
                            <span>{next.location[lang]}</span>
                            <ArrowRight size={14} />
                        </div>
                    </div>
                </Link>
            </section>
        </main>
    );
};

export default CaseDetail;
